import { dmsToDecimal, resolveUtmFromTable } from './auto-detect';
import { convertToWGS84, allUtmZones, CRS_OPTIONS } from './coordinates';
import { ensureCrs, normalizeCrs } from './georef';

// ── XYZ builder ──

/** A user-facing import problem (shown as-is in the mapping dialog, toasts and layer status). */
export class XyzImportError extends Error {
  constructor(message) {
    super(message);
    this.name = 'XyzImportError';
  }
}

/** No real coordinate (degrees, metres or feet) is this large; bigger values are typos or IDs. */
export const MAX_ABS_COORD = 1e8;
const MAX_LISTED_ROWS = 5;

/** Geographic CRS codes: values must be lon/lat degrees. */
const GEOGRAPHIC_CRS = ['EPSG:4326', 'EPSG:4269'];

const SKIP_REASONS = {
  missing: 'missing coordinates',
  nonNumeric: 'not a number',
  tooLarge: 'value too large',
  outside: 'outside the coordinate system',
};

/** Display name of a CRS code ("UTM 10N", "WGS84"), else the code. */
export function crsDisplayLabel(code) {
  const hit = allUtmZones().find((z) => z.code === code) || CRS_OPTIONS.find((c) => c.code === code);
  return hit ? hit.label : String(code || '');
}

/** "rows 2, 5, 9 and 4 more" — 1-based data-row numbers (header excluded). */
export function listRows(rowNums, max = MAX_LISTED_ROWS, total = rowNums.length) {
  if (!rowNums.length) return '';
  const shownRows = rowNums.slice(0, max);
  const more = total - shownRows.length;
  return `${total === 1 ? 'row' : 'rows'} ${shownRows.join(', ')}${more > 0 ? ` and ${more} more` : ''}`;
}

/** Strict number parse: '' → null (missing); '12abc' → NaN; '41,87' (decimal comma) → 41.87. */
export function parseCoord(raw, format = 'dd') {
  if (raw === undefined || raw === null) return null;
  const s = String(raw).trim();
  if (s === '') return null;
  if (format === 'dms') return dmsToDecimal(s);
  const v = Number(/^-?\d+,\d+$/.test(s) ? s.replace(',', '.') : s);
  return Number.isNaN(v) ? NaN : v;
}

/**
 * One-line summary of skipped rows from `metadata.xyz` ("3 rows skipped: 2 missing
 * coordinates (rows 4, 9), 1 not a number (row 12)"), or '' when nothing was skipped.
 */
export function describeSkipped(xyz) {
  const n = xyz?.skippedRows || 0;
  if (!n) return '';
  const by = xyz.skippedByReason || {};
  const examples = xyz.skippedExamples || [];
  const parts = Object.entries(SKIP_REASONS).filter(([k]) => by[k]).map(([k, label]) => {
    const rows = examples.filter((e) => e.reason === k).map((e) => e.row);
    return `${by[k]} ${label}${rows.length ? ` (${listRows(rows, MAX_LISTED_ROWS, by[k])})` : ''}`;
  });
  return `${n} row${n === 1 ? '' : 's'} skipped${parts.length ? `: ${parts.join(', ')}` : ''}`;
}

/**
 * Build a GeoJSON FeatureCollection from tabular rows + a column mapping.
 * Attaches `metadata.xyz` describing the Z fields, the resolved CRS and the skipped rows.
 *
 * Throws XyzImportError (never returns an empty layer) when: the file has no data rows, UTM
 * auto finds no zone in the file, the CRS is unknown, a geographic CRS has values outside
 * ±90/±180 (listing the rows), or no row has valid coordinates (listing why per reason).
 */
export function buildXYZGeoJSON(headers, rows, mapping) {
  const { x, y, z = [], format = 'dd', extras = {} } = mapping;
  if (!rows || rows.length === 0) throw new XyzImportError('The file has no data rows');
  if (!(x >= 0 && x < headers.length) || !(y >= 0 && y < headers.length)) {
    throw new XyzImportError('Select both X and Y columns');
  }
  let crs = mapping.crs || 'EPSG:4326';
  if (crs === 'UTM_AUTO') {
    const hit = resolveUtmFromTable(headers, rows, x, y, `${mapping.fileName || ''} ${mapping.hint || ''}`);
    if (!hit) {
      throw new XyzImportError(
        'UTM auto zone could not find a location in this file. Choose the UTM zone (for example UTM 16N or EPSG:32616). Easting and northing do not name a zone.',
      );
    }
    crs = hit.code;
  }
  const code = normalizeCrs(crs);
  const geographic = GEOGRAPHIC_CRS.includes(code);
  const passthrough = code === 'EPSG:4326' || code === 'local';
  if (!passthrough && !ensureCrs(code)) {
    throw new XyzImportError(`Coordinate system ${crs} is not available. Pick one from the list or type its EPSG code.`);
  }

  const zNames = z.map(i => headers[i]);
  const features = [];
  const skippedByReason = { missing: 0, nonNumeric: 0, tooLarge: 0, outside: 0 };
  const skippedExamples = [];
  const outOfRange = [];
  const skip = (reason, ri) => {
    skippedByReason[reason]++;
    if (skippedExamples.length < 50) skippedExamples.push({ row: ri + 1, reason });
  };

  rows.forEach((row, ri) => {
    const px = parseCoord(row[x], format);
    const py = parseCoord(row[y], format);
    if (px === null || py === null) { skip('missing', ri); return; }
    if (Number.isNaN(px) || Number.isNaN(py)) { skip('nonNumeric', ri); return; }
    if (!Number.isFinite(px) || !Number.isFinite(py) || Math.abs(px) > MAX_ABS_COORD || Math.abs(py) > MAX_ABS_COORD) {
      skip('tooLarge', ri); return;
    }
    // V.43: a geographic CRS with impossible degrees is an error, not a projected guess
    if (geographic && (Math.abs(py) > 90 || Math.abs(px) > 180)) { outOfRange.push(ri + 1); return; }

    let lon = px, lat = py;
    if (!passthrough) {
      try {
        ({ lon, lat } = convertToWGS84(px, py, code));
      } catch {
        skip('outside', ri); return;
      }
    }
    if (!Number.isFinite(lon) || !Number.isFinite(lat) || Math.abs(lat) > 90 || Math.abs(lon) > 180) { skip('outside', ri); return; }

    const properties = { _row: ri };
    headers.forEach((h, i) => {
      if (i === x || i === y) return;
      const raw = row[i];
      const num = parseFloat(raw);
      properties[h] = (!isNaN(num) && isFinite(num) && String(raw).trim() !== '') ? num : (raw ?? '');
    });
    // Canonical extra fields
    Object.entries(extras).forEach(([key, colIdx]) => {
      if (colIdx >= 0 && row[colIdx] !== undefined) properties[`_${key}`] = row[colIdx];
    });

    features.push({
      type: 'Feature',
      properties,
      geometry: { type: 'Point', coordinates: [lon, lat] },
    });
  });

  if (outOfRange.length) {
    throw new XyzImportError(
      `${outOfRange.length} ${outOfRange.length === 1 ? 'row has' : 'rows have'} latitude/longitude outside ±90°/±180° (${listRows(outOfRange)}). `
      + `${crsDisplayLabel(code)} needs degrees: fix those rows, or choose the projected coordinate system (e.g. a UTM zone) the file uses.`,
    );
  }

  const skipped = Object.values(skippedByReason).reduce((a, b) => a + b, 0);
  const xyz = {
    zFields: zNames,
    activeZ: zNames[0],
    crs, format,
    totalRows: rows.length,
    skippedRows: skipped,
    skippedByReason,
    skippedExamples,
    mapping: { x: headers[x], y: headers[y], z: zNames, extras },
  };

  if (features.length === 0) {
    const why = describeSkipped(xyz).replace(/^\d+ rows? skipped: /, '');
    const hint = skippedByReason.outside
      ? ` Check the coordinate system: ${crsDisplayLabel(code)} does not fit these values.`
      : skippedByReason.nonNumeric ? ` Check that ${headers[x]} and ${headers[y]} are the coordinate columns.` : '';
    throw new XyzImportError(`No rows have valid coordinates (${why}).${hint}`);
  }

  return { type: 'FeatureCollection', features, metadata: { xyz } };
}
